import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Paper as MuiPaper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Helmet } from "react-helmet-async";
import styled from "@emotion/styled";
import { Block, Upgrade } from "@mui/icons-material";

import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import {
  fetchAllSubscriptions,
  cancelSubscription,
  updateSubscription,
  SubscriptionStatus,
  ManagedSubscription,
  CancelReason,
  CANCEL_REASON_LABELS,
} from "../../redux/slices/subscription";
import { fetchAllPlans } from "../../redux/slices/plan";
import { createSubscriptionInvoice } from "../../redux/slices/subscriptionInvoice";
import SubscriptionPlanDialog, {
  resolveBillingPeriod,
} from "../../components/superadmin/SubscriptionPlanDialog";

const Paper = styled(MuiPaper)(spacing);

const statusLabel = (status: string) => {
  switch (status) {
    case "trialing":
      return "Kokeilu";
    case "active":
      return "Aktiivinen";
    case "past_due":
      return "Erääntynyt";
    case "canceled":
      return "Peruttu";
    case "suspended":
      return "Jäädytetty";
    case "expired":
      return "Vanhentunut";
    default:
      return status;
  }
};

const statusColor = (
  status: string
): "warning" | "success" | "error" | "default" | "primary" => {
  switch (status) {
    case "active":
      return "success";
    case "trialing":
      return "primary";
    case "past_due":
      return "warning";
    case "canceled":
    case "expired":
    case "suspended":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (val: string | null | undefined) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fi-FI");
};

/**
 * Tooltip text for the status chip. The only place a collected cancellation
 * reason surfaces — without a read surface the field would just accumulate
 * unseen. Empty string means MUI renders no tooltip at all.
 */
const cancellationSummary = (sub: ManagedSubscription) => {
  if (sub.status !== "canceled") return "";
  const parts = [];
  if (sub.canceledAt) parts.push(`Peruttu ${formatDate(sub.canceledAt)}`);
  if (sub.cancelReason) parts.push(CANCEL_REASON_LABELS[sub.cancelReason]);
  if (sub.cancelReasonText) parts.push(`"${sub.cancelReasonText}"`);
  return parts.length ? parts.join(" — ") : "Syytä ei kirjattu";
};

const formatAmount = (cents: number | null | undefined) => {
  if (cents == null) return "0,00";
  return (cents / 100).toLocaleString("fi-FI", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const SuperadminSubscriptionManagement = () => {
  const dispatch = useAppDispatch();
  const { allSubscriptions, allSubscriptionsStatus } = useAppSelector(
    (state) => state.subscription
  );

  const { plans } = useAppSelector((state) => state.plan);

  const [cancelTarget, setCancelTarget] = useState<ManagedSubscription | null>(
    null
  );
  const [planTarget, setPlanTarget] = useState<ManagedSubscription | null>(
    null
  );
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState<CancelReason | "">("");
  const [cancelReasonText, setCancelReasonText] = useState("");

  useEffect(() => {
    dispatch(fetchAllSubscriptions());
    dispatch(fetchAllPlans());
  }, [dispatch]);

  const closeCancelDialog = () => {
    setCancelTarget(null);
    setCancelReason("");
    setCancelReasonText("");
  };

  const handleCancel = async () => {
    if (!cancelTarget || !cancelReason) return;
    setWorking(true);
    setError(null);
    try {
      await dispatch(
        cancelSubscription(
          cancelTarget.id,
          true,
          cancelReason,
          cancelReasonText.trim() || undefined
        )
      );
      closeCancelDialog();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Tilauksen peruutus epäonnistui.");
    } finally {
      setWorking(false);
    }
  };

  /**
   * Activates the subscription (or swaps the plan of an active one) and
   * optionally issues the subscription invoice. Writes every field the billing
   * workers read: status alone would leave currentPeriodEnd NULL, which the
   * renewal scan skips and the expiry sweep never matches.
   *
   * Reactivating a cancelled association goes through here too. Its voided
   * invoices stay voided — reactivation does not resurrect old debt.
   */
  const handlePlanConfirm = async (planId: number, createInvoice: boolean) => {
    if (!planTarget) return;
    const associationId = planTarget.association?.id;
    if (!associationId) {
      setPlanError("Tilaukselta puuttuu tiekunta.");
      return;
    }

    setWorking(true);
    setPlanError(null);
    try {
      const period = resolveBillingPeriod(planTarget);

      await dispatch(
        updateSubscription(planTarget.id, {
          associationId,
          planId,
          status: SubscriptionStatus.ACTIVE,
          trialStart: null,
          trialEnd: null,
          currentPeriodStart: period.start.toISOString(),
          currentPeriodEnd: period.end.toISOString(),
        })
      );

      if (createInvoice) {
        const plan = plans.find((p) => p.id === planId);
        const dueAt = new Date();
        dueAt.setDate(dueAt.getDate() + 7);
        // amountCents is the monthly price — the backend multiplies it by 12.
        // Note it does not link the invoice to the subscription, so marking it
        // paid won't move the period; the update above already set it.
        await dispatch(
          createSubscriptionInvoice(
            {
              number: `INV-${Date.now()}`,
              issuedAt: new Date().toISOString(),
              dueAt: dueAt.toISOString(),
              amountCents: plan?.priceCents,
              periodEnd: period.end.toISOString(),
              status: "generating",
              subscriptionId: planTarget.id,
            } as any,
            associationId
          )
        );
      }

      dispatch(fetchAllSubscriptions());
      setPlanTarget(null);
    } catch (e: any) {
      setPlanError(
        e?.response?.data?.message ?? "Tilauksen päivitys epäonnistui."
      );
    } finally {
      setWorking(false);
    }
  };

  const isLoading = allSubscriptionsStatus === "loading";

  return (
    <>
      <Helmet title="Tilausten hallinta" />
      <Typography variant="h3" gutterBottom>
        Tilausten hallinta
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Vaihda tiekunnan paketti tai aktivoi kokeilu maksavaksi tilaukseksi, tai
        peru tilaus (peruutus mitätöi avoimet laskut).
      </Typography>

      <Paper mt={4}>
        {isLoading ? (
          <Box p={6} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tiekunta</TableCell>
                  <TableCell>Tila</TableCell>
                  <TableCell>Paketti</TableCell>
                  <TableCell>Kausi päättyy</TableCell>
                  <TableCell align="right">Avoimet (€)</TableCell>
                  <TableCell align="right">Toiminnot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Ei tilauksia
                    </TableCell>
                  </TableRow>
                ) : (
                  allSubscriptions.map((sub) => {
                    const cancelled =
                      sub.status === SubscriptionStatus.CANCELED ||
                      sub.status === SubscriptionStatus.EXPIRED;
                    return (
                      <TableRow key={sub.id} hover>
                        <TableCell>{sub.association?.name ?? "—"}</TableCell>
                        <TableCell>
                          <Tooltip title={cancellationSummary(sub)}>
                            <Chip
                              label={statusLabel(sub.status)}
                              color={statusColor(sub.status)}
                              size="small"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{sub.plan?.name ?? "—"}</TableCell>
                        <TableCell>
                          {formatDate(sub.currentPeriodEnd)}
                        </TableCell>
                        <TableCell align="right">
                          {sub.outstanding?.count
                            ? `${formatAmount(sub.outstanding.sumCents)} (${
                                sub.outstanding.count
                              })`
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={
                              cancelled
                                ? "Palauta aktiiviseksi"
                                : "Vaihda paketti / aktivoi"
                            }
                          >
                            <IconButton
                              size="small"
                              color={cancelled ? "success" : "primary"}
                              onClick={() => {
                                setPlanError(null);
                                setPlanTarget(sub);
                              }}
                            >
                              <Upgrade fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!cancelled && (
                            <Tooltip title="Peru tilaus">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setError(null);
                                  setCancelTarget(sub);
                                }}
                              >
                                <Block fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <SubscriptionPlanDialog
        subscription={planTarget}
        plans={plans}
        working={working}
        error={planError}
        onClose={() => setPlanTarget(null)}
        onConfirm={handlePlanConfirm}
      />

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => !working && closeCancelDialog()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Peru tilaus</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Tiekunnan <strong>{cancelTarget?.association?.name}</strong> tilaus
            merkitään perutuksi. Tiekunta menettää sovelluksen ominaisuudet.
          </Typography>
          {cancelTarget?.outstanding?.count ? (
            <Typography variant="body2" color="text.secondary">
              Lisäksi {cancelTarget.outstanding.count} avointa laskua (
              {formatAmount(cancelTarget.outstanding.sumCents)} €) mitätöidään.
            </Typography>
          ) : null}

          <FormControl sx={{ mt: 2 }}>
            <FormLabel id="superadmin-cancel-reason-label">
              Peruutuksen syy
            </FormLabel>
            <RadioGroup
              aria-labelledby="superadmin-cancel-reason-label"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value as CancelReason)}
            >
              {Object.values(CancelReason).map((reason) => (
                <FormControlLabel
                  key={reason}
                  value={reason}
                  control={<Radio size="small" />}
                  label={CANCEL_REASON_LABELS[reason]}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {cancelReason === CancelReason.OTHER && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Kerro lyhyesti"
              value={cancelReasonText}
              onChange={(e) => setCancelReasonText(e.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{ mt: 1 }}
            />
          )}

          {error && (
            <Typography variant="body2" color="error" mt={2}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={working}>
            Sulje
          </Button>
          <Button
            onClick={handleCancel}
            disabled={working || !cancelReason}
            variant="contained"
            color="error"
          >
            {working ? "Perutaan…" : "Peru tilaus"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SuperadminSubscriptionManagement;
